import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  Button,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

import { useStatus } from "../../popup/NotificationContext";
import {
  CUSTOM_ROWS_PER_PAGE_OPTIONS,
  getDisplayRange,
  getPaginatedRows,
  getRowsPerPageLabel,
  getTotalPagesForRows,
  ROWS_PER_PAGE_MENU_PROPS,
} from "../../utils/tablePagination";
import {
  useAddSensorMutation,
  useDeleteSensorMutation,
  useGetSensorsQuery,
  useUpdateSensorMutation,
} from "../../redux/logistics/logisticsApi";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const SENSOR_TYPES = ["Fuel", "Temperature", "Load", "Door", "Other"];

const toNumberOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeCalibration = (calibration) => {
  if (!Array.isArray(calibration)) return [];
  return calibration
    .map((row) => ({
      value: row?.value ?? row?.fuel ?? "",
      voltage: row?.voltage ?? "",
    }))
    .map((row) => ({
      value: row.value === 0 ? 0 : row.value || "",
      voltage: row.voltage === 0 ? 0 : row.voltage || "",
    }));
};

const EmptyState = () => (
  <Stack spacing={1} alignItems="center" sx={{ py: 10, color: "#6b7280" }}>
    <Box
      sx={{
        width: 92,
        height: 92,
        borderRadius: "50%",
        bgcolor: "#fff1f2",
        display: "grid",
        placeItems: "center",
        color: "#fb7185",
        fontSize: 34,
        userSelect: "none",
      }}
    >
      ✕
    </Box>
    <Typography sx={{ fontWeight: 700 }}>No Data Available In Table</Typography>
  </Stack>
);

export default function SensorPortalModal({ open = true, onClose }) {
  const { showStatus } = useStatus();
  const { data: sensorsPayload = [], refetch } = useGetSensorsQuery(undefined, { skip: !open });
  const [addSensor, { isLoading: isAdding }] = useAddSensorMutation();
  const [updateSensor, { isLoading: isUpdating }] = useUpdateSensorMutation();
  const [deleteSensor, { isLoading: isDeleting }] = useDeleteSensorMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [mode, setMode] = useState("table"); // table | create | edit
  const [editingSensor, setEditingSensor] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState(null);

  const [form, setForm] = useState({
    name: "",
    type: "Fuel",
    size: "",
    calibration: [{ value: "", voltage: "" }],
  });

  const sensors = useMemo(() => {
    const list = Array.isArray(sensorsPayload) ? sensorsPayload : sensorsPayload?.data || [];
    return Array.isArray(list) ? list : [];
  }, [sensorsPayload]);

  const normalizedSensors = useMemo(
    () =>
      sensors.map((sensor) => ({
        raw: sensor,
        id: sensor?._id || sensor?.id,
        name: sensor?.name || "-",
        type: sensor?.type || "-",
        size: sensor?.size || "0",
        calibration: normalizeCalibration(sensor?.calibration),
        calibrationCount: Array.isArray(sensor?.calibration) ? sensor.calibration.length : 0,
      })),
    [sensors]
  );

  const filteredSensors = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return normalizedSensors;
    return normalizedSensors.filter((item) => {
      return [item.name, item.type, item.size].some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [normalizedSensors, searchQuery]);

  const totalPages = getTotalPagesForRows(filteredSensors.length, rowsPerPage);
  const pagedSensors = useMemo(() => getPaginatedRows(filteredSensors, page, rowsPerPage), [filteredSensors, page, rowsPerPage]);
  const displayRange = useMemo(() => getDisplayRange(filteredSensors.length, page, rowsPerPage), [filteredSensors.length, page, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  useEffect(() => {
    if (!open) return;
    if (mode === "table") return;
    if (!editingSensor) return;

    const raw = editingSensor.raw || editingSensor;
    setForm({
      name: String(raw?.name || ""),
      type: String(raw?.type || "Fuel"),
      size: String(raw?.size || ""),
      calibration: normalizeCalibration(raw?.calibration).length ? normalizeCalibration(raw?.calibration) : [{ value: "", voltage: "" }],
    });
  }, [editingSensor, mode, open]);

  const resetForm = () => {
    setForm({
      name: "",
      type: "Fuel",
      size: "",
      calibration: [{ value: "", voltage: "" }],
    });
  };

  const closeForm = () => {
    resetForm();
    setEditingSensor(null);
    setMode("table");
  };

  const addCalibrationRow = () => {
    setForm((prev) => ({
      ...prev,
      calibration: [...prev.calibration, { value: "", voltage: "" }],
    }));
  };

  const removeCalibrationRow = (index) => {
    setForm((prev) => {
      const next = prev.calibration.filter((_, idx) => idx !== index);
      return { ...prev, calibration: next.length ? next : [{ value: "", voltage: "" }] };
    });
  };

  const updateCalibrationRow = (index, key, value) => {
    setForm((prev) => {
      const next = prev.calibration.map((row, idx) => (idx === index ? { ...row, [key]: value } : row));
      return { ...prev, calibration: next };
    });
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showStatus("warning", "Name required hai.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      type: form.type,
      size: form.size,
      calibration: (Array.isArray(form.calibration) ? form.calibration : [])
        .map((row) => ({
          value: toNumberOrNull(row.value),
          voltage: toNumberOrNull(row.voltage),
        }))
        .filter((row) => row.value != null && row.voltage != null),
    };

    try {
      if (mode === "edit") {
        const id = editingSensor?.id || editingSensor?.raw?._id || editingSensor?._id;
        if (!id) throw new Error("Sensor id missing");
        await updateSensor({ id, ...payload }).unwrap();
        showStatus("success", "Sensor updated.");
      } else {
        await addSensor(payload).unwrap();
        showStatus("success", "Sensor added.");
      }

      await refetch();
      closeForm();
    } catch (error) {
      showStatus("error", error?.data?.error || error?.data?.msg || "Sensor save failed.");
    }
  };

  const handleDelete = async (sensor) => {
    const id = sensor?.id || sensor?.raw?._id || sensor?._id;
    if (!id) return;
    if (!window.confirm("Sensor delete karna hai?")) return;
    try {
      await deleteSensor(id).unwrap();
      showStatus("success", "Sensor deleted.");
      if (selectedSensor?.id === id) setSelectedSensor(null);
    } catch (error) {
      showStatus("error", error?.data?.error || "Failed to delete sensor.");
    }
  };

  const busy = isAdding || isUpdating || isDeleting;
  const showCalibrationPanel = mode === "table" && Boolean(selectedSensor);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "76vh" } }}>
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
            {mode === "table" ? "Sensors List" : "Add Sensor"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {mode === "table" ? (
          <Box sx={{ display: "grid", gridTemplateColumns: showCalibrationPanel ? "1fr 340px" : "1fr", minHeight: "68vh" }}>
            <Box sx={{ borderRight: showCalibrationPanel ? "1px solid #e5e7eb" : "none" }}>
              <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "360px auto" }, gap: 1.5, alignItems: "center" }}>
                  <TextField
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    sx={inputSx}
                    InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton onClick={() => { setMode("create"); setEditingSensor(null); resetForm(); }} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                      <AddIcon />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>

              <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }} MenuProps={ROWS_PER_PAGE_MENU_PROPS}>
                    {CUSTOM_ROWS_PER_PAGE_OPTIONS.map((size) => (
                      <MenuItem key={size} value={size}>
                        {getRowsPerPageLabel(size)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={1}>
                  <IconButton onClick={() => refetch()} sx={{ color: "#6b7280" }}>
                    <RefreshIcon />
                  </IconButton>
                </Stack>
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "50vh", borderRadius: 0 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {["Name", "Sensor Type", "Size", "Calibration Count", "Action"].map((head) => (
                        <TableCell key={head} sx={{ fontWeight: 700, color: "#6b7280", bgcolor: "#fff", borderBottom: "1px solid #d1d5db" }}>
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedSensors.length ? (
                      pagedSensors.map((sensor) => (
                        <TableRow
                          key={sensor.id}
                          hover
                          onClick={() => setSelectedSensor(sensor)}
                          sx={{ cursor: "pointer", bgcolor: selectedSensor?.id === sensor.id ? "#f8fafc" : undefined }}
                        >
                          <TableCell sx={{ fontWeight: 800 }}>{sensor.name}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{String(sensor.type || "").toLowerCase()}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{sensor.size}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{sensor.calibrationCount}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Edit Sensor">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingSensor(sensor);
                                    setMode("edit");
                                  }}
                                  sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                >
                                  <EditOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Sensor">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(sensor)}
                                  sx={{ bgcolor: "#ef4444", borderRadius: 1 }}
                                  disabled={busy}
                                >
                                  <DeleteOutlineIcon sx={{ color: "#fff", fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <EmptyState />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ color: "#374151" }}>
                  Showing {displayRange.from} to {displayRange.to} of {filteredSensors.length} entries
                </Typography>
                <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
              </Box>
            </Box>

            {showCalibrationPanel ? (
              <Box sx={{ bgcolor: "#fff", display: "flex", flexDirection: "column" }}>
                <Box sx={{ px: 2, py: 1.75, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontWeight: 900 }}>Voltage Calibration</Typography>
                  <IconButton size="small" onClick={() => setSelectedSensor(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Divider />
                <Box sx={{ px: 2, py: 1.5, flex: 1, overflow: "auto" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, color: "#6b7280", fontWeight: 700, fontSize: 13 }}>
                    <span>Fuel</span>
                    <span>Voltage</span>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  {selectedSensor?.calibration?.length ? (
                    <Stack spacing={1.25}>
                      {selectedSensor.calibration.map((row, idx) => (
                        <Box key={`${selectedSensor.id}-cal-${idx}`} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                          <Typography sx={{ fontWeight: 700 }}>{row.value}</Typography>
                          <Typography sx={{ fontWeight: 700 }}>{row.voltage}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ color: "#6b7280" }}>No calibration points</Typography>
                  )}
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ borderTop: "1px solid #e5e7eb" }}>
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5 }}>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Name</Typography>
                  <TextField
                    fullWidth
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Sensor Type</Typography>
                  <FormControl fullWidth>
                    <Select value={form.type} onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))} sx={inputSx}>
                      {SENSOR_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Size</Typography>
                  <TextField
                    fullWidth
                    value={form.size}
                    onChange={(event) => setForm((prev) => ({ ...prev, size: event.target.value }))}
                    sx={inputSx}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 2.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ color: "#6b7280", fontWeight: 800 }}>Fuel Calibration</Typography>
                  <IconButton size="small" onClick={addCalibrationRow} sx={{ color: "#111827" }}>
                    <AddBoxOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 60px" }, gap: 2 }}>
                  {form.calibration.map((row, idx) => (
                    <React.Fragment key={`cal-${idx}`}>
                      <Box>
                        <Typography sx={{ mb: 0.75, fontWeight: 700 }}>Fuel</Typography>
                        <TextField
                          fullWidth
                          value={row.value}
                          onChange={(event) => updateCalibrationRow(idx, "value", event.target.value)}
                          sx={inputSx}
                          type="number"
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ mb: 0.75, fontWeight: 700 }}>Voltage</Typography>
                        <TextField
                          fullWidth
                          value={row.voltage}
                          onChange={(event) => updateCalibrationRow(idx, "voltage", event.target.value)}
                          sx={inputSx}
                          type="number"
                        />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "end", pb: 0.5 }}>
                        <IconButton
                          onClick={() => removeCalibrationRow(idx)}
                          sx={{ color: "#ef4444" }}
                          disabled={form.calibration.length <= 1}
                        >
                          <DeleteForeverOutlinedIcon />
                        </IconButton>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: 3, pb: 2.5, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={busy}
                sx={{
                  minWidth: 220,
                  bgcolor: "#f43f5e",
                  "&:hover": { bgcolor: "#e11d48" },
                  fontWeight: 800,
                  borderRadius: 1.2,
                }}
              >
                {busy ? "SUBMIT..." : "SUBMIT"}
              </Button>
              <Button
                variant="contained"
                onClick={closeForm}
                disabled={busy}
                sx={{
                  minWidth: 220,
                  bgcolor: "#6b7280",
                  "&:hover": { bgcolor: "#4b5563" },
                  fontWeight: 800,
                  borderRadius: 1.2,
                }}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
